import { Link, type Href } from 'expo-router';
import { XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Modal, Pressable, View } from 'react-native';

import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';

export type ProfileAffiliation = Readonly<{
  avatarUrl?: string | null;
  href: Href;
  id: string;
  initials: string;
  name: string;
  subtitle?: string | null;
}>;

export function ProfileAffiliations({
  description,
  emptyText,
  items,
  modalTitle,
  title,
}: Readonly<{
  description: string;
  emptyText: string;
  items: ProfileAffiliation[];
  modalTitle: string;
  title: string;
}>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const previewItems = items.slice(0, 5);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="gap-4">
          {items.length === 0 ? (
            <Text variant="muted">{emptyText}</Text>
          ) : (
            <Pressable
              accessibilityRole="button"
              className="flex-row items-center justify-between gap-4 rounded-md border border-border p-3 active:opacity-80"
              onPress={() => setIsOpen(true)}
            >
              <View className="min-w-0 flex-1 gap-1">
                <Text variant="large">{items.length}</Text>
                <Text variant="muted">Tap to view all</Text>
              </View>
              <View className="h-12 flex-row items-center pr-2">
                {previewItems.map((item, index) => (
                  <Avatar
                    key={item.id}
                    alt={`${item.name} avatar`}
                    className="size-11 border-2 border-card"
                    style={{ marginLeft: index === 0 ? 0 : -12 }}
                  >
                    {item.avatarUrl ? <AvatarImage source={{ uri: item.avatarUrl }} /> : null}
                    <AvatarFallback>
                      <Text className="font-sans text-sm font-bold">{item.initials}</Text>
                    </AvatarFallback>
                  </Avatar>
                ))}
              </View>
            </Pressable>
          )}
        </CardContent>
      </Card>

      <Modal
        animationType="slide"
        onRequestClose={() => setIsOpen(false)}
        presentationStyle="pageSheet"
        visible={isOpen}
      >
        <View className="flex-1 gap-4 bg-background p-6">
          <View className="flex-row items-center justify-between gap-4">
            <View className="min-w-0 flex-1">
              <Text variant="h2" numberOfLines={2}>
                {modalTitle}
              </Text>
              <Text variant="muted">{items.length} active</Text>
            </View>
            <Button
              accessibilityLabel={`Close ${modalTitle}`}
              onPress={() => setIsOpen(false)}
              size="icon"
              variant="secondary"
            >
              <XIcon color="white" size={18} />
            </Button>
          </View>

          <View className="gap-3">
            {items.map((item) => (
              <Link key={item.id} href={item.href} asChild>
                <Pressable
                  className="flex-row items-center gap-3 rounded-md border border-border p-3 active:bg-muted"
                  onPress={() => setIsOpen(false)}
                >
                  <Avatar alt={`${item.name} avatar`} className="size-12">
                    {item.avatarUrl ? <AvatarImage source={{ uri: item.avatarUrl }} /> : null}
                    <AvatarFallback>
                      <Text className="font-sans font-bold">{item.initials}</Text>
                    </AvatarFallback>
                  </Avatar>
                  <View className="min-w-0 flex-1">
                    <Text variant="large" numberOfLines={2}>
                      {item.name}
                    </Text>
                    {item.subtitle ? (
                      <Text variant="muted" numberOfLines={1}>
                        {item.subtitle}
                      </Text>
                    ) : null}
                  </View>
                </Pressable>
              </Link>
            ))}
          </View>
        </View>
      </Modal>
    </>
  );
}
